import React from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { loadRestaurantAsync, updateRestaurantPoolScheduleAsync, editPoolSchedule, startEditingPoolSchedule, cancelEditingPoolSchedule } from "../../../../actions/restaurantDetails";
import Loader from "../../../../components/loader";
import PoolScheduleEditor from "./../poolScheduleEditor";
import { Link } from "react-router-dom";
import * as Badges from "../../../../components/badges";
import { canManageLegacyTakeawayPoolActions, isMagicEnabledFromSearch } from "../../../../helpers/tenant";

export class RestaurantInfo extends React.Component {
  componentDidMount() {
    this.props.loadRestaurantAsync(this.props.restaurantId);
  }

  onRestaurantPoolScheduleChange(schedule) {
    this.props.editPoolSchedule(this.props.restaurantId, schedule);
  }

  onRestaurantPoolScheduleEdit() {
    this.props.startEditingPoolSchedule(this.props.restaurantId);
  }

  onRestaurantPoolScheduleSave() {
    this.props.updateRestaurantPoolScheduleAsync(this.props.restaurantId, this.props.editedRestaurantPoolSchedule);
  }

  onRestaurantPoolScheduleCancel() {
    this.props.cancelEditingPoolSchedule(this.props.restaurantId);
  }

  render() {
    if (this.props.isLoading) {
      return <Loader />;
    } else if (this.props.restaurantError) {
      return <div className="alert alert-danger">{this.props.restaurantError}</div>;
    } else {
      let statusTag =
        this.props.restaurantStatus === "Offline" ? (
          <Badges.Offline />
        ) : this.props.restaurantStatus === "TempOffline" ? (
          <Badges.TempOffline />
        ) : this.props.restaurantStatus === "Online" ? (
          <Badges.Online />
        ) : (
          <Badges.Unknown />
        );

      const partnerSubscriptionsString = (subscriptions) => {
        if (!subscriptions || subscriptions.length === 0) return "-";
        return subscriptions.map((s) => `${s.type || "-"}: ${s.name}`).join(", ");
      };
      const canEditPoolSchedule = canManageLegacyTakeawayPoolActions(this.props.tenant, this.props.isMagicEnabled);
      const editScheduleTooltip = canEditPoolSchedule ? "" : "Function disabled for legacy Takeaway countries.";
      const editScheduleClassName = canEditPoolSchedule ? "" : "legacy-disabled-action";
      const restaurantPoolPath = this.props.restaurantPool ? {
        pathname: "/pools/" + encodeURIComponent(this.props.restaurantPool.id),
        search: this.props.locationSearch,
      } : null;

      return (
        <React.Fragment>
          <h2 className="section-title">{this.props.restaurantName}</h2>
          <dl className="row">
            <dt className="col-sm-3">ID</dt>
            <dd className="col-sm-9">
              <a href={this.props.restaurantUrl}>{this.props.restaurantId}</a>
            </dd>

            <dt className="col-sm-3">Status</dt>
            <dd className="col-sm-9">{statusTag}</dd>

            <dt className="col-sm-3">Current Pool</dt>
            <dd className="col-sm-9">{this.props.restaurantPool ? <Link to={restaurantPoolPath}>{this.props.restaurantPool.name}</Link> : "No pool"}</dd>

            <dt className="col-sm-3">Subscriptions</dt>
            <dd className="col-sm-9">{partnerSubscriptionsString(this.props.restaurantPartnerSubscriptions)}</dd>
          </dl>

          <div className="row">
            <div className="col-sm-12 col-lg-6">
              <h3 className="subsection-title">Pool Schedule</h3>
              <div className="edit-button-bar">
                {this.props.isEditing ? (
                  <React.Fragment>
                    <button id="btnCancel" onClick={() => this.onRestaurantPoolScheduleCancel()} className="btn btn-sm btn-secondary mr-1" disabled={this.props.isSaving}>
                      Cancel
                    </button>
                    <button id="btnSave" onClick={() => this.onRestaurantPoolScheduleSave()} className="btn btn-sm btn-success" disabled={this.props.isSaving}>
                      Save
                    </button>
                  </React.Fragment>
                ) : (
                  <span title={editScheduleTooltip}>
                    <button id="btnEdit" className={`btn btn-sm btn-primary ${editScheduleClassName}`.trim()} disabled={!canEditPoolSchedule} onClick={() => this.onRestaurantPoolScheduleEdit()}>
                      Edit schedule
                    </button>
                  </span>
                )}
                {this.props.isSaving ? <Loader /> : null}
              </div>
            </div>
          </div>
          <PoolScheduleEditor
            poolSchedule={this.props.editedRestaurantPoolSchedule}
            savedPoolSchedule={this.props.restaurantPoolSchedule}
            disabled={this.props.isSaving}
            isEditing={this.props.isEditing}
            onPoolScheduleChange={this.onRestaurantPoolScheduleChange.bind(this)}
          />
        </React.Fragment>
      );
    }
  }
}

RestaurantInfo.propTypes = {
  isEditing: PropTypes.bool,
  isLoading: PropTypes.bool,
  isSaving: PropTypes.bool,

  restaurantId: PropTypes.number,
  restaurantName: PropTypes.string,
  restaurantPartnerSubscriptions: PropTypes.array,
  restaurantPoolSchedule: PropTypes.array,
  editedRestaurantPoolSchedule: PropTypes.array,
  restaurantError: PropTypes.string,
  restaurantUrl: PropTypes.string,
  restaurantStatus: PropTypes.string,
  restaurantPool: PropTypes.object,
  tenant: PropTypes.string,
  isMagicEnabled: PropTypes.bool,
  locationSearch: PropTypes.string,

  loadRestaurantAsync: PropTypes.func,
  updateRestaurantPoolScheduleAsync: PropTypes.func,
  editPoolSchedule: PropTypes.func,
  startEditingPoolSchedule: PropTypes.func,
  cancelEditingPoolSchedule: PropTypes.func,
};

const mapStateToProps = (state, props) => {
  return {
    searchText: state.restaurantSearch.searchText,
    searchType: state.restaurantSearch.searchType,

    isEditing: state.restaurantDetails.isEditing,
    isLoading: state.restaurantDetails.isLoading,
    isSaving: state.restaurantDetails.isSaving,

    restaurantId: props.restaurantId,
    restaurantName: state.restaurantDetails.name,
    restaurantPartnerSubscriptions: state.restaurantDetails.partnerSubscriptions,
    restaurantPoolSchedule: state.restaurantDetails.poolSchedule,
    editedRestaurantPoolSchedule: state.restaurantDetails.editedPoolSchedule,
    restaurantError: state.restaurantDetails.loadError || state.restaurantDetails.updateError,
    restaurantUrl: state.restaurantDetails.url,
    restaurantStatus: state.restaurantDetails.status,
    restaurantPool: state.restaurantDetails.pool,
    tenant: state.system && state.system.tenant,
    isMagicEnabled: isMagicEnabledFromSearch(state.router && state.router.location && state.router.location.search),
    locationSearch: state.router && state.router.location && state.router.location.search,
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      loadRestaurantAsync,
      updateRestaurantPoolScheduleAsync,
      editPoolSchedule,
      startEditingPoolSchedule,
      cancelEditingPoolSchedule,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(RestaurantInfo);



// WEBPACK FOOTER //
// ./src/containers/restaurants/details/info/index.js